// ============================================================================
// LoginScreen — Phase 2.1.5
// ============================================================================
// Email/password sign-in +
// forgot-password flow. Uses the same supabase client as everything else.
// ============================================================================
import React, {useState} from 'react';
import Button from '../shared/Button.js';
import Field from '../shared/Field.js';
import InlineNotice from '../shared/InlineNotice.js';
import AuthScreenFrame from './AuthScreenFrame.js';

import {sb} from '../lib/supabase.js';
import {unwrapEdgeFunctionError, type EdgeFunctionErrorLike} from '../lib/edgeErrors.js';

type LoginScreenProps = {
  onLogin?: () => void;
};

function signInErrorMessage(message: string | null | undefined) {
  if (/invalid login credentials/i.test(message || '')) {
    return 'Invalid email or password. Use the email your WCF Planner account was created with.';
  }
  // Migration 218 bans a deactivated account's login, and Supabase Auth then
  // answers "User is banned". Show the deactivated-account wording instead
  // (Design Constitution Q39), the same words as InactiveAccountCard.
  if (/user is banned/i.test(message || '')) {
    return 'Your account is inactive — ask an admin';
  }
  return message || 'Could not sign in. Please try again.';
}

function LoginScreen(_props: LoginScreenProps) {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [mode, setMode] = useState('login'); // 'login' | 'reset'
  const [resetSent, setResetSent] = useState(false);

  async function handleLogin(e: React.FormEvent<HTMLFormElement>) {
    e.preventDefault();
    setLoading(true);
    setError('');
    const {error} = await sb.auth.signInWithPassword({email: email.trim(), password});
    if (error) {
      setError(signInErrorMessage(error.message));
      setLoading(false);
    }
  }

  async function handleReset(e: React.FormEvent<HTMLFormElement>) {
    e.preventDefault();
    setLoading(true);
    setError('');
    try {
      // Use our branded email via edge function instead of Supabase's default
      const {error: fnError} = await sb.functions.invoke('rapid-processor', {
        body: {type: 'password_reset', data: {email: email.trim()}},
      });
      if (fnError) throw fnError;
      setResetSent(true);
    } catch (err) {
      const msg = await unwrapEdgeFunctionError(err as EdgeFunctionErrorLike);
      setError(msg || 'Could not send reset email. Please try again.');
    }
    setLoading(false);
  }

  return (
    <div data-login-screen>
      <AuthScreenFrame title={mode === 'login' ? 'Sign In' : 'Reset Password'}>
        {mode === 'login' ? (
          <form onSubmit={handleLogin} className="wcf-auth-form">
            <Field label="Email" required>
              <input type="email" autoComplete="username" value={email} onChange={(e) => setEmail(e.target.value)} />
            </Field>
            <Field label="Password" required>
              <input
                type="password"
                autoComplete="current-password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
              />
            </Field>
            <InlineNotice notice={error ? {kind: 'error', message: error} : null} />
            <Button type="submit" variant="primary" fullWidth busy={loading} busyLabel="Signing In…">
              Sign In
            </Button>
            <button
              type="button"
              className="wcf-auth-link"
              onClick={() => {
                setError('');
                setResetSent(false);
                setMode('reset');
              }}
            >
              Forgot Password?
            </button>
          </form>
        ) : (
          <form onSubmit={handleReset} className="wcf-auth-form">
            {resetSent ? (
              <InlineNotice
                notice={{
                  kind: 'success',
                  message: 'If an account exists, a reset link has been sent. Check your email.',
                }}
              />
            ) : (
              <>
                <p className="wcf-auth-help">
                  Enter your WCF Planner account email. If an account exists, we'll send a reset link.
                </p>
                <Field label="Email" required>
                  <input
                    type="email"
                    autoComplete="username"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                  />
                </Field>
                <InlineNotice notice={error ? {kind: 'error', message: error} : null} />
                <Button type="submit" variant="primary" fullWidth busy={loading} busyLabel="Sending…">
                  Send Reset Link
                </Button>
              </>
            )}
            <button
              type="button"
              className="wcf-auth-link"
              onClick={() => {
                setError('');
                setMode('login');
                setResetSent(false);
              }}
            >
              Back To Sign In
            </button>
          </form>
        )}
      </AuthScreenFrame>
    </div>
  );
}
export default LoginScreen;
